import React, { useContext, useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { useListApi } from "@/hooks/useListApi";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { CustomPageSelector, CustomPagination } from "../CustomPagination";
import {
  ScientificResearch,
  scientificResearchApi,
  ScientificResearchFolder,
  scientificResearchFolderApi,
} from "@/api/superAdmin";
import { useScientificResearchColumn } from "./hooks";
import { DynamicSelector } from "../DynamicSelector";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { CustomPaginationInfo } from "../CustomPaginationInfo";
import { AuthContext, AuthContextType } from "@/context/AuthContext";

function ScientificResearchTable() {
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;
  const { planDetails } = contextValues;
  const [selectedFolder, selectFolder] = useState<string>();
  const { t } = useTranslation();
  const {
    loading: loadingList,
    apiResult,
    search,
    pagination,
  } = useListApi<ScientificResearch>({
    baseApiObject: scientificResearchApi,
    defaultParams: {
      folder: selectedFolder === "all" ? undefined : selectedFolder,
      api_key: planDetails?.api_key,
    },
    enable: !!planDetails?.api_key,
  });

  const {
    search: folderSearch,
    loadNext,
    list: scientificResearchFolders,
  } = useListApi<ScientificResearchFolder>({
    baseApiObject: scientificResearchFolderApi,
    enable: !!planDetails?.api_key,
    defaultParams: { api_key: planDetails?.api_key },
  });

  const columns = useScientificResearchColumn();

  return (
    <div className="w-full my-8">
      <h3 className="text-3xl text-center">
        {t("common.label.scientific_research")}
      </h3>
      <div className="sm:w-[410px] border-black my-4">
        <DynamicSelector
          onValueChange={selectFolder}
          value={selectedFolder || undefined}
          options={scientificResearchFolders.map((item) => ({
            label: item.name,
            value: item.id.toString(),
          }))}
          className="selector"
          onLoadNext={loadNext}
          onSearch={(searchValue) => {
            folderSearch.set(searchValue);
          }}
          includeAllOption={true}
          placeholder={t("common.placeholder.select")}
        />
      </div>
      <div className="flex gap-2 w-full my-4 md:w-[410px]">
        <Input
          value={search.value}
          type="text"
          placeholder={t("common.placeholder.title")}
          onChange={(event) => search.set(event.target.value)}
        />
        {search.value && (
          <Button onClick={() => search.set("")}>
            {t("common.button.clear")}
          </Button>
        )}
      </div>
      {loadingList ? (
        <div className="flex min-h-[300px] md:min-h-[450px] items-center justify-center max-h-auto">
          <Loader />
        </div>
      ) : (
        <>
          <div className="min-h-[300px] md:min-h-[450px] max-h-auto">
            <DataTable columns={columns} data={apiResult?.results || []} />
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex flex-col md:flex-row md:w-2/3   md:space-x-4 mt-3">
                <CustomPageSelector
                  pageSize={pagination.pageSize}
                  onPageSizeChange={(value) => {
                    pagination.setPageSize(value);
                    pagination.setPage(1);
                  }}
                />
                <CustomPaginationInfo
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                />
              </div>
              <div className="flex md:space-x-4 mt-4 pr-5 pt-2">
                <CustomPagination
                  totalCount={apiResult?.count || 0}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.page}
                  onPageChange={(value) => pagination.setPage(value)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ScientificResearchTable;
